import { type RefObject, useEffect, useState } from "react";
import { highlightTargetClass } from "./eventTargeting.ts";

export type HighlightedChip = {
  /**
   * The chip element for the highlighted event, or null when nothing is
   * rendered for it. Populated even while the chip is scrolled out of view, so
   * callers that anchor to it can scroll it in first (see `isVisible`).
   */
  el: HTMLElement | null;
  /** Whether the chip currently intersects the container's visible area. */
  isVisible: boolean;
};

const NO_CHIP: HighlightedChip = { el: null, isVisible: false };

/** Return how much of `rect` is visible within `bounds`. */
function getIntersectionArea(rect: DOMRect, bounds: DOMRect): number {
  const width = Math.max(0, Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left));
  const height = Math.max(0, Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top));
  return width * height;
}

/**
 * Whether `next` describes the same chip as `current`. Lets the hook keep its
 * snapshot's object identity and skip a re-render when nothing changed — which
 * matters because FullCalendar's own churn re-fires the observers constantly.
 */
function isSameChip(current: HighlightedChip, next: HighlightedChip): boolean {
  return current.el === next.el && current.isVisible === next.isVisible;
}

/**
 * Find and track the highlighted event's chip inside one calendar container.
 * FullCalendar owns the target DOM, so observers bridge its mounts, relayouts,
 * and internal scrolling into a single element handle for React.
 *
 * The chip is located by the target class `CalendarView` stamps on the event
 * matching `targetKey` (its `instanceKey`), which is how a deep link anchors a
 * popover to it. Reports no chip when the grid is highlighting a different
 * event, which is the honest answer: the caller's target isn't on screen to
 * point at.
 */
export function useHighlightedChip(
  targetKey: string | undefined,
  containerRef: RefObject<HTMLElement | null>,
): HighlightedChip {
  const [chip, setChip] = useState<HighlightedChip>(NO_CHIP);

  useEffect(() => {
    const container = containerRef.current;
    if (!targetKey || !container) {
      setChip((current) => (current === NO_CHIP ? current : NO_CHIP));
      return;
    }

    // Re-observe the found chip for size changes across re-measures; the chip is
    // a fresh DOM node each time FullCalendar relays out the day.
    let chipEl: HTMLElement | null = null;
    // Wrapped so the reference to `scheduleMeasure` (declared below) resolves at
    // call time; arrows keep `container`'s post-guard narrowing, which a hoisted
    // function declaration would lose.
    const resizeObserver = new ResizeObserver(() => scheduleMeasure());

    const measure = () => {
      const containerRect = container.getBoundingClientRect();
      // Scoped to the chips stamped for *this* target, not every highlighted
      // chip: the grid takes a single highlight, so another caller's highlight
      // would otherwise hand back its chip and anchor this one to the wrong
      // event. Carousel mode stamps the class on multiple (off-screen) slot
      // calendars; take the instance with the greatest visible area inside this
      // container. A translated off-screen slot still has a non-zero box, so
      // size alone does not identify the copy the user can see.
      const els = container.querySelectorAll<HTMLElement>(
        `.fc-event.${highlightTargetClass(targetKey)}`,
      );
      let bestMatch: { el: HTMLElement; visibleArea: number } | null = null;
      for (const el of els) {
        const visibleArea = getIntersectionArea(el.getBoundingClientRect(), containerRect);
        if (visibleArea > (bestMatch?.visibleArea ?? 0)) {
          bestMatch = { el, visibleArea };
        }
      }
      // Nothing on screen: still report the first stamped chip (scrolled out of
      // the day's visible window) so an anchoring caller can scroll it in, with
      // `isVisible` false to say it must scroll first.
      const offscreenEl = bestMatch === null ? (els[0] ?? null) : null;

      const next: HighlightedChip = bestMatch
        ? { el: bestMatch.el, isVisible: true }
        : { el: offscreenEl, isVisible: false };

      const foundEl = next.el;
      if (chipEl !== foundEl) {
        if (chipEl) resizeObserver.unobserve(chipEl);
        chipEl = foundEl;
        if (foundEl) resizeObserver.observe(foundEl);
      }
      setChip((current) => (isSameChip(current, next) ? current : next));
    };

    // Coalesce bursts into one measurement per animation frame: FullCalendar
    // mutates classes and scrolls constantly, and each measure() reads layout, so
    // measuring per event would force repeated synchronous reflows.
    let rafId = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    // The chip appears either by mounting — after we navigate to the event's day
    // and its events fetch resolves, an unbounded delay — or, when already on that
    // day, by `is-highlighted` being stamped in place on the existing chip. Watch
    // childList (the mount) plus the class and style attributes: class catches the
    // in-place stamp; style catches same-size repositions that fire neither
    // ResizeObserver nor scroll (the carousel slides a slot via `transform`).
    // Overlays portalled into this same container re-fire the observer with their
    // own mounts and style writes; the isSameChip guard in measure() bounds that.
    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    resizeObserver.observe(container);
    // Capture phase, scoped to the container, so scrolls inside FullCalendar's
    // own scroller are caught (scroll events don't bubble) without re-measuring
    // on unrelated chat-panel scrolls.
    container.addEventListener("scroll", scheduleMeasure, true);
    // A carousel slide animates `transform` over ~240ms; the style mutation above
    // fires only at its start (when the box is still pre-animation), so re-measure
    // once the transition settles. transitionend bubbles, so no capture needed.
    container.addEventListener("transitionend", scheduleMeasure);
    measure();

    return () => {
      cancelAnimationFrame(rafId);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      container.removeEventListener("scroll", scheduleMeasure, true);
      container.removeEventListener("transitionend", scheduleMeasure);
    };
  }, [targetKey, containerRef]);

  return chip;
}
